import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ArrowLeft, User as UserIcon, Save } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { Eye, EyeOff } from "lucide-react";
import { authService } from "@/services";
import { toast } from "@/hooks/use-toast";

const EditProfile = () => {
  const navigate = useNavigate();
  const { user, setUser } = useAuth();

  const [fullName, setFullName] = useState<string>("");
  const [email, setEmail] = useState<string>("");
  const [phoneNumber, setPhoneNumber] = useState<string>("");
  const [password, setPassword] = useState<string>("");
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [showPassword, setShowPassword] = useState(false);
  const [confirmPassword, setConfirmPassword] = useState("");
  const [isPasswordMatched, setIsPasswordMatched] = useState<boolean | null>(
    null
  );
  const [passwordMessage, setPasswordMessage] = useState("");

  const handlePasswordChange = (value: string) => {
    setPassword(value);
    if (!confirmPassword) {
      setIsPasswordMatched(null);
      setPasswordMessage("");
      return;
    }
    if (value === confirmPassword) {
      setIsPasswordMatched(true);
      setPasswordMessage("Password cocok ✅");
    } else {
      setIsPasswordMatched(false);
      setPasswordMessage("Password tidak sama ❌");
    }
  };

  const handleConfirmPasswordChange = (value: string) => {
    setConfirmPassword(value);
    if (!value) {
      setIsPasswordMatched(null);
      setPasswordMessage("");
      return;
    }
    if (value === password) {
      setIsPasswordMatched(true);
      setPasswordMessage("Password cocok ✅");
    } else {
      setIsPasswordMatched(false);
      setPasswordMessage("Password tidak sama ❌");
    }
  };

  useEffect(() => {
    if (user) {
      setFullName(user.name || "");
      setEmail(user.email || "");
      setPhoneNumber(user.phone || "");
    }
  }, [user]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if ((password || confirmPassword) && password !== confirmPassword) {
      toast({
        title: "Password mismatch",
        description: "Passwords do not match.",
        variant: "destructive",
      });
      return;
    }

    if (password.length < 3 && password != "") {
      toast({
        title: "Invalid Password",
        description: "Password must be at least 3 characters",
        variant: "destructive"
      })
      return;
    }

    if (fullName.length > 60) {
      toast({
        title: "Invalid Name",
        description: "Name must be lower than 60 characters",
        variant: "destructive",
      });
      return;
    }

    console.log("handleSubmit triggered 🚀");
    setIsSubmitting(true);

    try {
      const payload: any = {
        name: fullName,
        email,
        phone: phoneNumber,
        roleID: "0", // TO DO
        organizationID: "0",
      };

      if (password && password.trim() !== "") {
        payload.password = password;
      }

      const response = await authService.updateProfile(payload);

      if (response.success == "1") {
        console.log("Profile updated successfully");

        // Construct updated user object from form input
        const updatedUser = {
          ...user, // keep existing fields like id
          name: fullName,
          email: email,
          phone: phoneNumber,
        };

        // Update state and localStorage
        setUser(updatedUser);
        localStorage.setItem("user", JSON.stringify(updatedUser));

        toast({
          title: "Success",
          description: "Profile Successfully updated!",
          variant: "default",
        });
        navigate("/profile");
        return;
      } else {
        toast({
          title: "Failed",
          description: response.message,
          variant: "destructive",
        });
        console.error("Profile update failed:", response.message);
      return;

      }
    } catch (error) {
      toast({
        title: "Error",
        description: "Something went wrong. Please try again.",
        variant: "destructive",
      });
      console.error("Failed to update profile", error);
      return;

    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-white via-white to-green-100">
      {/* Header */}
      <header className="p-4">
        <div className="max-w-2xl mx-auto flex items-center gap-4">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => navigate(-1)}
            className="rounded-full hover:bg-black/5"
          >
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Edit Profile</h1>
            <p className="text-sm text-muted-foreground">
              Update your account information
            </p>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-2xl mx-auto p-6 pb-24">
        <Card className="shadow-lg border border-black/5">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <UserIcon className="w-5 h-5" />
              Account Details
            </CardTitle>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="space-y-2">
                <Label htmlFor="fullName">Full Name</Label>
                <p className="text-xs text-muted-foreground">
                  Panjang nama maksimal 60 karakter
                </p>
                <Input
                  id="fullName"
                  value={fullName}
                  onChange={(e) => setFullName(e.target.value)}
                  placeholder="Your name"
                  required
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="email">Email</Label>
                <Input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@example.com"
                  required
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="phoneNumber">Phone Number</Label>
                <Input
                  id="phoneNumber"
                  type="text"
                  placeholder="0812345678"
                  value={phoneNumber}
                  onChange={(e) => {
                    let value = e.target.value.replace(/[^0-9]/g, ""); // digits only
                    if (value && !value.startsWith("08")) {
                      value = "08" + value.replace(/^0+/, "");
                    }
                    setPhoneNumber(value);
                  }}
                  required
                  minLength={10}
                  maxLength={13}
                />
              </div>

              {/* Password with toggle */}
              <div className="space-y-2">
                <Label htmlFor="password">Password</Label>
                <p className="text-xs text-muted-foreground">
                  Panjang password minimal 3 karakter
                </p>
                <div className="relative">
                  <Input
                    id="password"
                    type={showPassword ? "text" : "password"}
                    value={password}
                    onChange={(e) => handlePasswordChange(e.target.value)}
                    placeholder="Enter new password"
                    autoComplete="new-password"
                    className="pr-10"
                    required={!!confirmPassword}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute inset-y-0 right-0 flex items-center pr-3 text-gray-500 hover:text-gray-700"
                  >
                    {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                  </button>
                </div>
              </div>

              {/* Confirm password with validation */}
              <div className="space-y-2 mt-3">
                <Label htmlFor="confirmPassword">Confirm Password</Label>
                {passwordMessage && (
                  <p
                    className={`text-sm font-medium ${isPasswordMatched ? "text-green-600" : "text-red-600"
                      }`}
                  >
                    {passwordMessage}
                  </p>
                )}
                <div className="relative">
                  <Input
                    id="confirmPassword"
                    type={showPassword ? "text" : "password"}
                    placeholder="Konfirmasi ulang password"
                    value={confirmPassword}
                    onChange={(e) =>
                      handleConfirmPasswordChange(e.target.value)
                    }
                    className={`pr-10 ${isPasswordMatched === null
                        ? ""
                        : isPasswordMatched
                          ? "border-green-500 focus:border-green-600"
                          : "border-red-500 focus:border-red-600"
                      }`}
                    required={!!password}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute inset-y-0 right-0 flex items-center pr-3 text-gray-500 hover:text-gray-700"
                  >
                    {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                  </button>
                </div>
              </div>

              <div className="flex items-center gap-3 justify-end pt-4">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => navigate("/profile")}
                >
                  Cancel
                </Button>
                <Button type="submit" disabled={isSubmitting}>
                  <Save className="w-4 h-4 mr-2" />
                  {isSubmitting ? "Saving..." : "Save Changes"}
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      </main>
    </div>
  );
};

export default EditProfile;
